import {
  generateError,
  getCurrentServiceId,
  isDHPError,
  Logger
} from 'src/utils/helpers'
import {
  SILENT_RETRY_MAX_ATTEMPTS,
  SILENT_RETRY_DELAY_MS,
  SILENT_RETRY_DHP_ERROR_TYPES
} from 'src/config/constants'

class DeviceHttpProxyClient {
  //private attributes
  #plugin
  #sessionId
  #setError

  constructor(plugin, sessionId, setError) {
    this.#plugin = plugin
    this.#sessionId = sessionId
    this.#setError = setError
  }

  async request(
    payload,
    useCache,
    handleError,
    parseData = true,
    parser = (data) => data,
    attempt = 0
  ) {
    Logger.log(`Fetching DHP - payload ${JSON.stringify(payload)}`)
    const response = await this.#plugin(payload, useCache)
    const payloadData = payload?.body?.data || null
    const handleParser = (data, isBase64Encoded) => {
      return parser(isBase64Encoded ? atob(data) : data)
    }

    const shouldSilentRetry =
      attempt < SILENT_RETRY_MAX_ATTEMPTS &&
      isDHPError(response) &&
      SILENT_RETRY_DHP_ERROR_TYPES.includes(response.errorType)

    if (shouldSilentRetry) {
      Logger.warn(
        `DHP silent retry ${attempt + 1}/${SILENT_RETRY_MAX_ATTEMPTS} - errorType: ${response.errorType}`
      )
      await new Promise((resolve) => setTimeout(resolve, SILENT_RETRY_DELAY_MS))
      return this.request(
        payload,
        useCache,
        handleError,
        parseData,
        parser,
        attempt + 1
      )
    }

    if (handleError && isDHPError(response)) {
      this.#setError(
        generateError({
          ...response,
          payload: payloadData,
          resourceUri: payload.path
        })
      )
      return null
    }

    if (response?.body?.data) {
      try {
        response.body.data = parseData
          ? handleParser(response.body.data, response.body.isBase64Encoded)
          : response.body.data
      } catch (e) {
        if (handleError) {
          this.#setError(
            generateError({
              errorType: 'unableToDecode',
              reason: e,
              payload: payloadData,
              resourceUri: payload.path
            })
          )
          return null
        }
      }
    }

    Logger.log(`Fetched DHP - response ${JSON.stringify(response)}`)
    return response
  }

  async fetch(
    href,
    useCache = false,
    parseData = false,
    handleError = true,
    parser = null
  ) {
    const options = {
      path: href,
      sessionId: this.#sessionId,
      method: 'GET',
      allowUserInteraction: true,
      calledBy: getCurrentServiceId()
    }
    return this.request(options, useCache, handleError, parseData, parser)
  }

  async write(href, payload, method, handleError = true, customOptions = {}) {
    let options = {
      path: href,
      sessionId: this.#sessionId,
      method: method,
      body: { data: payload },
      allowUserInteraction: true,
      calledBy: getCurrentServiceId()
    }
    options = {
      ...options,
      ...customOptions
    }

    return this.request(options, false, handleError)
  }
}

export default DeviceHttpProxyClient
